import create from 'zustand'
import { CascaderOptionType } from 'antd/lib/cascader'
import { labelSorter } from '../../shared/sortUtils'
import { transformForAntDesignCascaderNetworkCompanyCountry } from '../../shared/networkCompanySelectorUtils'
import { GroupedNetworkWithCompaniesWithLegalEntitiesAndCountries, Country } from '../../types'

export enum PyramidTabs {
  Level = 'pyramids-level',
  Age = 'pyramids-age',
}

export interface PyramidDate {
  year: number
  month: number
}

export type PyramidSelectorValue = {
  networkId: string
  companyId: string
  country: Country
}

interface PyramidsStoreState {
  storeInitialized: boolean
  showCurrent: boolean
  showPast: boolean
  showForecast: boolean
  pastDate: PyramidDate
  forecastDate: PyramidDate
  activeTab: PyramidTabs
  newtworksWithCompanyAndCountries: GroupedNetworkWithCompaniesWithLegalEntitiesAndCountries[]
  newtworksWithCompanyAndCountriesSelection: CascaderOptionType[]
  newtworksWithCompanyAndCountriesSelectionCurrentValue: string[]
  selected: PyramidSelectorValue
  isAgePyramidVisible: boolean
  isCareerPathEnabled: boolean

  initStore: (newtworksWithCompanyAndCountries: GroupedNetworkWithCompaniesWithLegalEntitiesAndCountries[]) => void
  resetStore: () => void
  setShowCurrent: (showCurrent: boolean) => void
  setShowPast: (showPast: boolean) => void
  setShowForecast: (showForecast: boolean) => void
  setPastDate: (pastDate: PyramidDate) => void
  setForecastDate: (forecastDate: PyramidDate) => void
  setActiveTab: (activeTab: PyramidTabs) => void
  setSelected: (networkId: string, companyId: string, country: Country) => void
  setIsAgePyramidVisible: (isAgePyramidVisible: boolean) => void
  setIsCareerPathEnabled: (isCareerPathEnabled: boolean) => void
}

const initialValues = {
  storeInitialized: false,
  showCurrent: true,
  showPast: false,
  showForecast: false,
  pastDate: {
    year: new Date().getUTCFullYear(),
    month: new Date().getUTCMonth(),
  },
  forecastDate: {
    year: new Date().getUTCFullYear(),
    month: new Date().getUTCMonth(),
  },
  activeTab: PyramidTabs.Level,
  newtworksWithCompanyAndCountries: [],
  newtworksWithCompanyAndCountriesSelection: [],
  newtworksWithCompanyAndCountriesSelectionCurrentValue: [],
  selected: {
    networkId: '',
    companyId: '',
    country: '' as Country,
  },
  isAgePyramidVisible: false,
  isCareerPathEnabled: false,
}

export const usePyramidsStore = create<PyramidsStoreState>((set) => ({
  ...initialValues,

  initStore: (_newtworksWithCompanyAndCountries: GroupedNetworkWithCompaniesWithLegalEntitiesAndCountries[]) =>
    set((state) => {
      const newtworksWithCompanyAndCountries = _newtworksWithCompanyAndCountries
        .map((network) => {
          return {
            ...network,
            companies: network.companies
              .map((company) => {
                return {
                  ...company,
                  companyCountries: company.companyCountries
                    .slice()
                    .sort((country1, country2) => labelSorter(country1.country, country2.country)),
                }
              })
              .sort((company1, company2) => labelSorter(company1.companyName, company2.companyName)),
          }
        })
        .sort((network1, network2) => labelSorter(network1.networkName, network2.networkName))

      const newtworksWithCompanyAndCountriesSelection = transformForAntDesignCascaderNetworkCompanyCountry(newtworksWithCompanyAndCountries)

      const networkId = state.selected.networkId || newtworksWithCompanyAndCountries[0].networkId
      const companyId = state.selected.companyId || newtworksWithCompanyAndCountries[0].companies[0].companyId
      const country = state.selected.country || (newtworksWithCompanyAndCountries[0].companies[0].companyCountries[0].country as Country)

      const selected = {
        networkId,
        companyId,
        country,
      }

      const newtworksWithCompanyAndCountriesSelectionCurrentValue = [networkId, companyId, country]

      return {
        storeInitialized: true,
        newtworksWithCompanyAndCountries,
        newtworksWithCompanyAndCountriesSelection,
        newtworksWithCompanyAndCountriesSelectionCurrentValue,
        selected,
      }
    }),

  resetStore: () => set({ ...initialValues }),

  setIsAgePyramidVisible: (isAgePyramidVisible: boolean) => set({ isAgePyramidVisible }),

  setIsCareerPathEnabled: (isCareerPathEnabled: boolean) => set({ isCareerPathEnabled }),

  setShowCurrent: (showCurrent: boolean) => set({ showCurrent }),

  setShowPast: (showPast: boolean) => set({ showPast }),

  setShowForecast: (showForecast: boolean) => set({ showForecast }),

  setPastDate: (pastDate: PyramidDate) => set({ pastDate }),

  setForecastDate: (forecastDate: PyramidDate) => set({ forecastDate }),

  setActiveTab: (activeTab: PyramidTabs) => set({ activeTab }),

  setSelected: (networkId: string, companyId: string, country: Country) => set({ selected: { networkId, companyId, country } }),
}))
